import { IComponentOptions, IFilterService, IRootScopeService, IScope } from "angular";
import { take } from "rxjs";
import { IComment } from "@gtmhub/comments";
import { storage } from "@gtmhub/core/storage";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { GoalsChangedEventArgs, OkrEvents } from "@gtmhub/okrs/events";
import { formatAttainmentProgressValue } from "@gtmhub/okrs/metrics/utils";
import { Ng1ConfirmDeleteService } from "@gtmhub/shared/services/ng1-confirm-delete.service";
import { SizeOfGif } from "@gtmhub/shared/services/tenor-gif.service";
import { CurrentUserService, getCurrentUserId } from "@gtmhub/users";
import { ICustomField } from "@webapp/custom-fields/models/custom-fields.models";
import { filterCustomFieldsBySession } from "@webapp/custom-fields/utils/custom-fields-filter-util";
import { localize } from "@webapp/localization/utils/localization.utils";
import { UpdateMetricNoteComponent, UpdateMetricNoteModalData } from "@webapp/okrs/metrics/components/update-metric-note/update-metric-note.component";
import { IMetricSnapshot, Metric } from "@webapp/okrs/metrics/models/metric.models";
import { cancelButton, primaryButton } from "@webapp/shared/modal/modal.utils";
import { UiModalService } from "@webapp/ui/modal/services/modal.service";
import { MetricValueFilter } from "../../filters/metric-value.filter";
import { MetricService } from "../../services/metric.service";

type SnapshotOrigin = "cascading_update" | "automatic_update" | "manual_update" | "notes_update" | "unsupported";

interface IMetricSnapshotPreviewBindings {
  snapshot: IMetricSnapshot;
  metric: Metric;
  sourceMetric?: Metric;
  formatAttainmentProgressValue(progress: number): number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface MetricSnapshotPreviewCtrl extends IMetricSnapshotPreviewBindings {}
export class MetricSnapshotPreviewCtrl {
  public isAdmin: boolean;
  public sizeOfGif = SizeOfGif;
  public currentUserId: string;
  public confidenceOverflow = "no-overflow";

  public static $inject = [
    "$filter",
    "CurrentUserService",
    "$scope",
    "$rootScope",
    "MetricService",
    "UIErrorHandlingService",
    "Ng1UiModalService",
    "Ng1ConfirmDeleteService",
  ];

  private okrEvents: OkrEvents;

  constructor(
    private $filter: IFilterService,
    currentUserService: CurrentUserService,
    private $scope: IScope,
    private $rootScope: IRootScopeService,
    private metricService: MetricService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private modalService: UiModalService,
    private confirmDeleteService: Ng1ConfirmDeleteService
  ) {
    this.formatAttainmentProgressValue = formatAttainmentProgressValue;
    this.isAdmin = currentUserService.userHasRole("admin");
    this.currentUserId = getCurrentUserId();
    this.okrEvents = new OkrEvents(this.$scope);

    this.okrEvents.onGoalsChanged((args: GoalsChangedEventArgs) => {
      if (args.reason === "metricCommentEdit" && args.detail.metric?.id === this.metric.id) {
        const snapshotUpdate = (args.detail.metric.metricHistory || []).find((snapshot) => snapshot.id === this.snapshot.id);
        this.snapshot = Object.assign({}, this.snapshot, snapshotUpdate);
      }
    });
  }

  public getSnapshotOrigin(): SnapshotOrigin {
    const isUserAction = !!this.snapshot.changedBy;
    const isCascadingUpdate = !!this.snapshot.sourceInfo;
    const isCascadingMetric = this.metric.cascadeType !== "";
    const isDynamicMetric = (this.metric.sourceMetricId && this.sourceMetric.insightName !== "") || (!this.metric.sourceMetricId && this.metric.insightName !== "");
    const hasComment = !!this.snapshot.comment;

    if (isCascadingUpdate) {
      return "cascading_update";
    } else if (!isUserAction) {
      return "automatic_update";
    } else if ((isCascadingMetric || isDynamicMetric) && hasComment && this.snapshot.valueChange === 0) {
      return "notes_update";
    }

    return "manual_update";
  }

  public getFormattedValue(): string {
    return this.$filter<MetricValueFilter>("metricValue")(this.snapshot.metricValue, this.metric.format);
  }

  public confirmDeleteSnapshot(): void {
    const metricId = this.metric.id;
    const goalId = this.metric.goalId;
    const snapshotId = this.snapshot.id;

    this.confirmDeleteService.confirmDeleteItem({ itemKey: "key_result_update" }).subscribe(() => {
      this.metricService.deleteSnapshot(goalId, metricId, snapshotId).then(
        () => {
          const okrEvents = new OkrEvents(this.$rootScope);
          okrEvents.broadcastGoalsChanged("goalChanged");

          this.$rootScope.$broadcast("goalsChanged");
          this.$rootScope.$broadcast("commentDeleted", { targetId: snapshotId } as IComment);
        },
        (err) => this.uiErrorHandlingService.handleModal(err)
      );
    });
  }

  public filterBySessionId = (customFields: ICustomField[]): ICustomField[] => {
    return filterCustomFieldsBySession(this.metric.sessionId, customFields);
  };

  public isCustomFieldDisabled(): boolean {
    return true;
  }

  public openUpdateMetricModal(): void {
    const updateMetricNoteModal$ = this.modalService.create<UpdateMetricNoteComponent, UpdateMetricNoteModalData>({
      uiContent: UpdateMetricNoteComponent,
      uiTitle: localize("edit_KR_update_note"),
      uiData: {
        metricId: this.metric.id,
        checkinId: this.snapshot.id,
      },
      uiFooter: [
        primaryButton(localize("update"), {
          disabled: (instance) => !!instance.indicators.saving || instance.loading || instance.updateMetricNoteForm.pristine,
          onClick: (instance) => {
            storage.remove("modalCloseReason");
            return instance.save();
          },
        }),
        cancelButton({
          onClick: (instance, modalRef) => {
            storage.remove("modalCloseReason");
            modalRef.close();
          },
        }),
      ],
      uiWidth: 657,
    });

    updateMetricNoteModal$?.containerInstance.containerClick.pipe(take(1)).subscribe(() => {
      storage.set("modalCloseReason", "backdrop click");
    });

    updateMetricNoteModal$?.containerInstance.cancelTriggered.pipe(take(1)).subscribe(() => {
      storage.remove("modalCloseReason");
    });
  }
}

export const MetricSnapshotPreviewComponent: IComponentOptions = {
  controller: MetricSnapshotPreviewCtrl,
  template: require("./metric-snapshot-preview.component.html"),
  bindings: {
    snapshot: "<",
    metric: "<",
    sourceMetric: "<?",
  },
};
